/**
 * ============================================================
 * Hero Banner -- assets/hero-banner.js
 * Basari Eyewear - Dawn Theme (Online Store 2.0)
 * ============================================================
 *
 * ARCHITECTURE
 * A single IIFE exposes one namespaced entry point: window.HeroBanner.
 * On DOMContentLoaded it instantiates one HeroCarousel instance per
 * .hero-banner__carousel element found in the document.
 *
 * Each HeroCarousel instance is fully self-contained:
 *   - Reads all configuration from data-* attributes (never from Liquid)
 *   - Cleans up its own interval timer on destroy()
 *   - Handles: autoplay, pause-on-hover, prev/next arrows, dot pagination,
 *     touch swipe (pointer events), keyboard (arrow keys on focus), and
 *     Shopify theme-editor section:load / section:unload events.
 *
 * PERFORMANCE NOTES
 * - Only opacity transitions are used (CSS). JS never touches layout props.
 * - No polling loops; transitions use CSS + a single interval for autoplay.
 * - requestAnimationFrame is used only for the double-frame DOM flush trick
 *   when resetting slide positions to avoid a Flash Of Unstyled Content.
 *
 * INFINITE LOOP LOGIC
 * The slide list is finite (no DOM cloning). "Infinite loop" is achieved by
 * clamping the index with modulo arithmetic:
 *   nextIndex = (currentIndex + 1) % totalSlides
 *   prevIndex = (currentIndex - 1 + totalSlides) % totalSlides
 * The active/exiting CSS classes drive cross-fade transitions (opacity only).
 */

/* eslint-disable no-var */
(function () {
  'use strict';

  /* ------------------------------------------------------------------ */
  /* HeroCarousel Class                                                   */
  /* ------------------------------------------------------------------ */
  function HeroCarousel(el) {
    this.el       = el;
    this.track    = el.querySelector('.hero-banner__track');
    this.slides   = Array.from(el.querySelectorAll('.hero-banner__slide'));
    this.arrows   = {
      prev: el.querySelector('.hero-banner__arrow--prev'),
      next: el.querySelector('.hero-banner__arrow--next'),
    };
    this.dots     = Array.from(el.querySelectorAll('.hero-banner__dot'));

    /* Read merchant configuration from data attributes */
    this.autoplay     = el.dataset.autoplay === 'true';
    this.speed        = parseInt(el.dataset.autoplaySpeed, 10) || 5000; /* ms */
    this.pauseOnHover = el.dataset.pauseOnHover === 'true';

    this.current   = 0;
    this.timer     = null;
    this.isPaused  = false;

    /* Touch / pointer swipe state */
    this._swipe = { startX: 0, startY: 0, active: false };

    /* Only initialise when there are slides to cycle */
    if (this.slides.length === 0) return;

    this._bindEvents();
    this._goTo(0, false); /* Set initial state without animation */
    if (this.autoplay && this.slides.length > 1) this._startAutoplay();
  }

  /* ------------------------------------------------------------------
     Public API
     ------------------------------------------------------------------ */

  /** Navigate to a specific slide index (0-based). */
  HeroCarousel.prototype.goTo = function (index) {
    this._goTo(index, true);
  };

  /** Previous slide (wraps). */
  HeroCarousel.prototype.prev = function () {
    var prevIdx = (this.current - 1 + this.slides.length) % this.slides.length;
    this._goTo(prevIdx, true);
  };

  /** Next slide (wraps). */
  HeroCarousel.prototype.next = function () {
    var nextIdx = (this.current + 1) % this.slides.length;
    this._goTo(nextIdx, true);
  };

  /** Pause autoplay (e.g. on hover or focus). */
  HeroCarousel.prototype.pause = function () {
    this.isPaused = true;
    this._stopAutoplay();
  };

  /** Resume autoplay. */
  HeroCarousel.prototype.resume = function () {
    this.isPaused = false;
    if (this.autoplay && this.slides.length > 1) this._startAutoplay();
  };

  /** Clean up all event listeners and timers (called on theme-editor unload). */
  HeroCarousel.prototype.destroy = function () {
    this._stopAutoplay();
    this._unbindEvents();
  };

  /* ------------------------------------------------------------------
     Private: slide transition
     ------------------------------------------------------------------ */

  /**
   * Core transition.
   * @param {number}  index      Target slide index (0-based).
   * @param {boolean} animate    false on first render (no CSS transition).
   */
  HeroCarousel.prototype._goTo = function (index, animate) {
    if (index === this.current && animate) return;

    var outgoing = this.slides[this.current];
    var incoming = this.slides[index];

    /* Mark the outgoing slide as exiting so CSS fades it out */
    if (animate && outgoing && outgoing !== incoming) {
      outgoing.classList.remove('hero-banner__slide--active');
      outgoing.classList.add('hero-banner__slide--exiting');
      outgoing.setAttribute('aria-hidden', 'true');

      /* Remove --exiting class after the CSS transition duration (600ms) */
      var exitingSlide = outgoing;
      setTimeout(function () {
        exitingSlide.classList.remove('hero-banner__slide--exiting');
      }, 650);
    } else if (!animate && outgoing) {
      outgoing.classList.remove('hero-banner__slide--active');
      outgoing.setAttribute('aria-hidden', 'true');
    }

    /* Activate incoming slide */
    incoming.classList.add('hero-banner__slide--active');
    incoming.setAttribute('aria-hidden', 'false');

    /* Update current index */
    this.current = index;

    /* Sync pagination dots */
    this._syncDots(index);

    /* Reset autoplay interval so a manual interaction gets the full delay */
    if (animate && this.autoplay && !this.isPaused && this.slides.length > 1) {
      this._stopAutoplay();
      this._startAutoplay();
    }
  };

  /* ------------------------------------------------------------------
     Private: dots
     ------------------------------------------------------------------ */

  HeroCarousel.prototype._syncDots = function (activeIndex) {
    for (var i = 0; i < this.dots.length; i++) {
      var dot = this.dots[i];
      var isActive = i === activeIndex;
      dot.classList.toggle('hero-banner__dot--active', isActive);
      if (isActive) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    }
  };

  /* ------------------------------------------------------------------
     Private: autoplay timer
     ------------------------------------------------------------------ */

  HeroCarousel.prototype._startAutoplay = function () {
    var self = this;
    this._stopAutoplay(); /* Guard: never have two timers running */
    this.timer = setInterval(function () {
      if (!self.isPaused) self.next();
    }, this.speed);
  };

  HeroCarousel.prototype._stopAutoplay = function () {
    if (this.timer !== null) {
      clearInterval(this.timer);
      this.timer = null;
    }
  };

  /* ------------------------------------------------------------------
     Private: event binding
     ------------------------------------------------------------------ */

  /**
   * All event handlers are stored on `this._handlers` so they can be
   * removed verbatim by _unbindEvents() — necessary for theme-editor
   * section reload cycles.
   */
  HeroCarousel.prototype._bindEvents = function () {
    var self = this;

    this._handlers = {
      /* Previous arrow */
      onPrev: function () { self.prev(); },

      /* Next arrow */
      onNext: function () { self.next(); },

      /* Dot click — reads data-slide-index */
      onDot: function (e) {
        var idx = parseInt(e.currentTarget.dataset.slideIndex, 10);
        if (!isNaN(idx)) self.goTo(idx);
      },

      /* Pause / resume on hover */
      onMouseEnter: function () {
        if (self.pauseOnHover) self.pause();
      },
      onMouseLeave: function () {
        if (self.pauseOnHover) self.resume();
      },

      /* Keyboard arrow keys when the carousel container is focused */
      onKeydown: function (e) {
        if (e.key === 'ArrowLeft')  { self.prev(); e.preventDefault(); }
        if (e.key === 'ArrowRight') { self.next(); e.preventDefault(); }
      },

      /* Pointer-based swipe — works for touch and mouse */
      onPointerDown: function (e) {
        self._swipe.startX  = e.clientX;
        self._swipe.startY  = e.clientY;
        self._swipe.active  = true;
      },
      onPointerUp: function (e) {
        if (!self._swipe.active) return;
        self._swipe.active = false;

        var deltaX = e.clientX - self._swipe.startX;
        var deltaY = e.clientY - self._swipe.startY;

        /*
         * Swipe threshold: 40px horizontal AND the horizontal movement must
         * dominate vertical movement (ratio > 1.5) to avoid triggering slides
         * during a scroll gesture on touch devices.
         */
        var THRESHOLD = 40;
        if (Math.abs(deltaX) > THRESHOLD && Math.abs(deltaX) > Math.abs(deltaY) * 1.5) {
          if (deltaX < 0) {
            self.next();
          } else {
            self.prev();
          }
        }
      },
      onPointerCancel: function () {
        self._swipe.active = false;
      },
    };

    /* Arrows */
    if (this.arrows.prev) {
      this.arrows.prev.addEventListener('click', this._handlers.onPrev);
    }
    if (this.arrows.next) {
      this.arrows.next.addEventListener('click', this._handlers.onNext);
    }

    /* Dots */
    for (var i = 0; i < this.dots.length; i++) {
      this.dots[i].addEventListener('click', this._handlers.onDot);
    }

    /* Hover-pause */
    if (this.pauseOnHover) {
      this.el.addEventListener('mouseenter', this._handlers.onMouseEnter);
      this.el.addEventListener('mouseleave', this._handlers.onMouseLeave);
    }

    /* Keyboard: listen on the carousel wrapper */
    this.el.setAttribute('tabindex', '-1'); /* make it focusable */
    this.el.addEventListener('keydown', this._handlers.onKeydown);

    /* Swipe */
    this.el.addEventListener('pointerdown', this._handlers.onPointerDown);
    this.el.addEventListener('pointerup',   this._handlers.onPointerUp);
    this.el.addEventListener('pointercancel', this._handlers.onPointerCancel);
  };

  HeroCarousel.prototype._unbindEvents = function () {
    if (!this._handlers) return;

    if (this.arrows.prev) {
      this.arrows.prev.removeEventListener('click', this._handlers.onPrev);
    }
    if (this.arrows.next) {
      this.arrows.next.removeEventListener('click', this._handlers.onNext);
    }

    for (var i = 0; i < this.dots.length; i++) {
      this.dots[i].removeEventListener('click', this._handlers.onDot);
    }

    this.el.removeEventListener('mouseenter',   this._handlers.onMouseEnter);
    this.el.removeEventListener('mouseleave',   this._handlers.onMouseLeave);
    this.el.removeEventListener('keydown',      this._handlers.onKeydown);
    this.el.removeEventListener('pointerdown',  this._handlers.onPointerDown);
    this.el.removeEventListener('pointerup',    this._handlers.onPointerUp);
    this.el.removeEventListener('pointercancel', this._handlers.onPointerCancel);

    this._handlers = null;
  };

  /* ------------------------------------------------------------------ */
  /* Initialisation                                                        */
  /* ------------------------------------------------------------------ */

  /**
   * Map of section IDs to their HeroCarousel instances.
   * Stored so theme-editor events can destroy and re-create instances cleanly.
   */
  var instances = {};

  function initAll(root) {
    var carousels = (root || document).querySelectorAll('.hero-banner__carousel');
    for (var i = 0; i < carousels.length; i++) {
      var el = carousels[i];
      /* Derive a stable key from the closest section element's id */
      var section = el.closest('.hero-banner-section') || el.closest('.hero-banner');
      var key = section ? section.id : 'hero-banner-' + i;

      /* Avoid double-initialising the same element */
      if (instances[key]) continue;
      instances[key] = new HeroCarousel(el);
    }
  }

  function destroySection(sectionId) {
    if (instances[sectionId]) {
      instances[sectionId].destroy();
      delete instances[sectionId];
    }
  }

  /* Initial page load */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    /* DOM already ready (e.g. script loaded asynchronously) */
    initAll();
  }

  /* ------------------------------------------------------------------
     Shopify Theme Editor events
     Fired when a merchant adds, removes, or reorders sections in the editor.
     ------------------------------------------------------------------ */
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target) initAll(e.target);
  });

  document.addEventListener('shopify:section:unload', function (e) {
    if (e.detail && e.detail.sectionId) {
      destroySection('hero-banner-' + e.detail.sectionId);
    }
  });

  /* Expose a minimal public API (prevents global pollution) */
  window.HeroBanner = { instances: instances };
})();
